import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { CalendarDays, ExternalLink, LockKeyhole, MapPinned, ShieldCheck, Sparkles, Star, UserRoundCheck, Users } from 'lucide-react';
import { Header } from '@/components/Header';
import { AppointmentForm } from '@/components/AppointmentForm';
import { ImageSlider } from '@/components/ImageSlider';
import { FloatingBeautyElements } from '@/components/FloatingBeautyElements';
import { isVideoCarouselEnabled } from '@/lib/apiBaseUrl';
import { applySeo, applyStructuredData, INDEXABLE_ROBOTS } from '@/lib/seo';

const ServicesBeforeAfter = lazy(() => import('@/components/ServicesBeforeAfter').then(({ ServicesBeforeAfter }) => ({ default: ServicesBeforeAfter })));
const LocationsSection = lazy(() => import('@/components/LocationsSection').then(({ LocationsSection }) => ({ default: LocationsSection })));
const VideosSection = lazy(() => import('@/components/VideosSection').then(({ VideosSection }) => ({ default: VideosSection })));
const Footer = lazy(() => import('@/components/Footer').then(({ Footer }) => ({ default: Footer })));
const FloatingActionButtons = lazy(() => import('@/components/FloatingActionButtons').then(({ FloatingActionButtons }) => ({ default: FloatingActionButtons })));

const CLINIC_EXTERIOR_IMAGES = [
  '/images/optimized/ext/DSC09572.webp',
  '/images/optimized/ext/DSC09573.webp',
  '/images/optimized/ext/DSC09575.webp',
  '/images/optimized/ext/DSC09576.webp',
  '/images/optimized/ext/DSC09577.webp',
  '/images/optimized/ext/DSC09580.webp',
  '/images/optimized/ext/DSC09582.webp',
  '/images/optimized/ext/DSC09584.webp',
];

const CLINIC_RECEPTION_IMAGES = [
  '/images/optimized/acceuil/DSC09611.webp',
  '/images/optimized/acceuil/DSC09613.webp',
  '/images/optimized/acceuil/DSC09617.webp',
  '/images/optimized/acceuil/DSC09619.webp',
  '/images/optimized/acceuil/DSC09625.webp',
  '/images/optimized/acceuil/DSC09633.webp',
  '/images/optimized/acceuil/DSC09642.webp',
  '/images/optimized/acceuil/DSC09645.webp',
];

const CLINIC_MACHINE_IMAGES = [
  '/images/optimized/machines/DSC09679.webp',
  '/images/optimized/machines/DSC09681.webp',
  '/images/optimized/machines/DSC09682.webp',
  '/images/optimized/machines/DSC09683.webp',
  '/images/optimized/machines/DSC09686.webp',
  '/images/optimized/machines/DSC09697.webp',
  '/images/optimized/machines/DSC09709.webp',
  '/images/optimized/machines/DSC09711.webp',
  '/images/optimized/machines/DSC09748.webp',
  '/images/optimized/machines/DSC09750.webp',
  '/images/optimized/machines/DSC09762.webp',
  '/images/optimized/machines/DSC09776.webp',
  '/images/optimized/machines/DSC09781.webp',
  '/images/optimized/machines/DSC09786.webp',
  '/images/optimized/machines/DSC09787.webp',
  '/images/optimized/machines/DSC09791.webp',
  '/images/optimized/machines/DSC09797.webp',
  '/images/optimized/machines/DSC09798.webp',
  '/images/optimized/machines/DSC09800.webp',
  '/images/optimized/machines/DSC09811.webp',
  '/images/optimized/machines/DSC09812.webp',
  '/images/optimized/machines/DSC09814.webp',
];

const CLINIC_INTERIOR_IMAGES = [
  '/images/optimized/int/DSC09605.webp',
  '/images/optimized/int/DSC09607.webp',
  '/images/optimized/int/DSC09610.webp',
  '/images/optimized/int/DSC09646.webp',
  '/images/optimized/int/DSC09650.webp',
  '/images/optimized/int/DSC09655.webp',
  '/images/optimized/int/DSC09666.webp',
  '/images/optimized/int/DSC09670.webp',
  '/images/optimized/int/DSC09673.webp',
  '/images/optimized/int/DSC09676.webp',
  '/images/optimized/int/DSC09677.webp',
  '/images/optimized/int/DSC09693.webp',
  '/images/optimized/int/DSC09694.webp',
  '/images/optimized/int/DSC09701.webp',
  '/images/optimized/int/DSC09702.webp',
  '/images/optimized/int/DSC09704.webp',
  '/images/optimized/int/DSC09707.webp',
  '/images/optimized/int/DSC09710.webp',
  '/images/optimized/int/DSC09712.webp',
  '/images/optimized/int/DSC09713.webp',
  '/images/optimized/int/DSC09716.webp',
  '/images/optimized/int/DSC09719.webp',
  '/images/optimized/int/DSC09725.webp',
  '/images/optimized/int/DSC09731.webp',
  '/images/optimized/int/DSC09735.webp',
  '/images/optimized/int/DSC09740.webp',
  '/images/optimized/int/DSC09743.webp',
  '/images/optimized/int/DSC09751.webp',
  '/images/optimized/int/DSC09752.webp',
  '/images/optimized/int/DSC09777.webp',
  '/images/optimized/int/DSC09778.webp',
  '/images/optimized/int/DSC09779.webp',
];

const getResponsiveSrcSet = (src: string) =>
  `${src.replace(/\.webp$/, '-480.webp')} 480w, ${src} 640w`;

export default function Home() {
  const [clinicImageIndex, setClinicImageIndex] = useState(0);
  const [receptionImageIndex, setReceptionImageIndex] = useState(0);
  const [machineImageIndex, setMachineImageIndex] = useState(0);
  const [interiorImageIndex, setInteriorImageIndex] = useState(0);
  const [showDeferredContent, setShowDeferredContent] = useState(false);
  const deferredContentTriggerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const title = 'La clinique dédiée aux hommes à Casablanca et Rabat';

    applySeo({
      title,
      description:
        'La clinique dédiée aux hommes à Casablanca et Rabat propose des soins esthétiques, minceur, épilation laser et soins du visage.',
      robots: INDEXABLE_ROBOTS,
      path: '/',
    });
    applyStructuredData();
  }, []);

  useEffect(() => {
    const trigger = deferredContentTriggerRef.current;

    if (!trigger || !('IntersectionObserver' in window)) {
      setShowDeferredContent(true);
      return;
    }

    let observer: IntersectionObserver | undefined;
    let fallbackTimer: number | undefined;
    const revealContent = () => {
      setShowDeferredContent(true);
      observer?.disconnect();
      window.clearTimeout(fallbackTimer);
    };

    // Scroll restoration can place the marker above the viewport without
    // intersecting it. Reveal content once the reader reaches OR passes it.
    const revealIfReached = () => {
      if (trigger.getBoundingClientRect().top <= window.innerHeight + 50) {
        revealContent();
      }
    };
    observer = new IntersectionObserver(revealIfReached, {
      rootMargin: '0px 0px 50px',
    });
    // Do not leave the document shortened indefinitely on a restored page.
    fallbackTimer = window.setTimeout(revealContent, 1000);
    observer.observe(trigger);
    revealIfReached();
    window.addEventListener('pageshow', revealIfReached);

    return () => {
      observer?.disconnect();
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('pageshow', revealIfReached);
    };
  }, []);

  useEffect(() => {
    const sectionPathById: Record<string, string> = {
      appointment: '/appointement',
      beforeAfter: '/beforeAfter',
      expertise: '/expertise',
      location: '/location',
      videos: '/videos',
    };
    const sectionIdByPath = Object.fromEntries(Object.entries(sectionPathById).map(([sectionId, path]) => [path, sectionId]));
    const sectionIds = Object.keys(sectionPathById);

    const scrollToSection = (targetId: string) => {
      if (!sectionIds.includes(targetId)) {
        return;
      }

      if (targetId !== 'appointment') {
        setShowDeferredContent(true);
      }

      window.setTimeout(() => {
        const target = document.getElementById(targetId);

        if (!target) {
          return;
        }

        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY,
          behavior: 'smooth',
        });
      }, 100);
    };

    const updateUrlForSection = (targetId: string, mode: 'push' | 'replace' = 'push') => {
      const path = sectionPathById[targetId];

      if (!path) {
        return;
      }

      const nextUrl = `${path}${window.location.search}`;

      if (`${window.location.pathname}${window.location.search}` === nextUrl) {
        return;
      }

      if (mode === 'replace') {
        window.history.replaceState(null, '', nextUrl);
        return;
      }

      window.history.pushState(null, '', nextUrl);
    };

    const revealDeferredHashTarget = () => {
      const targetId = window.location.hash.slice(1);

      if (!sectionIds.includes(targetId)) {
        return;
      }

      scrollToSection(targetId);
      updateUrlForSection(targetId, 'replace');
    };

    const revealPathTarget = () => {
      const targetId = sectionIdByPath[window.location.pathname];

      if (!targetId) {
        return;
      }

      scrollToSection(targetId);
    };

    const handleSectionLinkClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      const anchor = event.target instanceof Element
        ? event.target.closest<HTMLAnchorElement>('a[href^="#"]')
        : null;

      if (!anchor) {
        return;
      }

      const targetId = anchor.hash.slice(1);

      if (!sectionIds.includes(targetId)) {
        return;
      }

      event.preventDefault();
      scrollToSection(targetId);
      updateUrlForSection(targetId);
    };

    const handleProgrammaticSectionNavigation = (event: Event) => {
      const targetId = (event as CustomEvent<{ targetId?: string }>).detail?.targetId;

      if (!targetId || !sectionIds.includes(targetId)) {
        return;
      }

      scrollToSection(targetId);
      updateUrlForSection(targetId);
    };

    revealDeferredHashTarget();
    revealPathTarget();
    document.addEventListener('click', handleSectionLinkClick);
    window.addEventListener('bloom:navigate-section', handleProgrammaticSectionNavigation);
    window.addEventListener('hashchange', revealDeferredHashTarget);

    return () => {
      document.removeEventListener('click', handleSectionLinkClick);
      window.removeEventListener('bloom:navigate-section', handleProgrammaticSectionNavigation);
      window.removeEventListener('hashchange', revealDeferredHashTarget);
    };
  }, []);

  useEffect(() => {
    const rotation = window.setInterval(() => {
      setClinicImageIndex((currentIndex) => (currentIndex + 1) % CLINIC_EXTERIOR_IMAGES.length);
      setReceptionImageIndex((currentIndex) => (currentIndex + 1) % CLINIC_RECEPTION_IMAGES.length);
      setMachineImageIndex((currentIndex) => (currentIndex + 1) % CLINIC_MACHINE_IMAGES.length);
      setInteriorImageIndex((currentIndex) => (currentIndex + 1) % CLINIC_INTERIOR_IMAGES.length);
    }, 3000);

    return () => window.clearInterval(rotation);
  }, []);

  return (
    <div className="min-h-screen flex flex-col text-white">
      <Header />
      
      <main className="flex-1">
        {/* Formulaire principal avec fond luxueux */}
        <section className="relative min-h-screen flex items-start justify-center px-4 pt-6 pb-2 md:pb-[1.25rem]">
          {/* Decorative background layer (clipped separately so it doesn't affect sticky positioning) */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Fond avec image optimisée et teinte noire transparente */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(/images/backgrounds/bg-optimized.webp)',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
            }}
          />
          {/* Teinte noire transparente comme dans le footer Beefarm */}
          <div className="absolute inset-0 bg-black/60"></div>

          {/* Floating animated elements - Plus visibles et mieux animées */}
          <div 
            className="absolute top-20 left-10 w-48 h-48 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(212, 175, 55, 0.4) 0%, rgba(212, 175, 55, 0.2) 50%, transparent 100%)',
              animation: 'floatBackground 20s ease-in-out infinite',
            }}
          ></div>
          
          <div 
            className="absolute bottom-32 right-20 w-64 h-64 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, rgba(212, 175, 55, 0.15) 50%, transparent 100%)',
              animation: 'floatBackgroundSlow 30s ease-in-out infinite',
            }}
          ></div>
          
          <div 
            className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(212, 175, 55, 0.35) 0%, rgba(212, 175, 55, 0.1) 50%, transparent 100%)',
              animation: 'floatBackgroundFast 15s ease-in-out infinite',
            }}
          ></div>

          {/* Floating Beauty Elements */}
          <FloatingBeautyElements />
          </div>

          {/* Contenu */}
          <div className="relative z-10 w-full max-w-5xl md:pb-[1.25rem]">
            <div className="h-[33rem] md:h-[36rem]">
              <ImageSlider />
            </div>
            <div id="appointment" className="relative z-40 -mt-42 translate-y-1 md:-mt-40 md:translate-y-0">
              <AppointmentForm isOpen={true} />
            </div>
          </div>
        </section>

        <div ref={deferredContentTriggerRef} aria-hidden="true" className="h-4" />

        {showDeferredContent && <Suspense fallback={null}>
        <section className="relative overflow-hidden border-y border-[#D4AF37]/20 bg-black/55 px-4 py-12 text-white md:py-16">
            <div className="relative z-10 mx-auto max-w-6xl">
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#D4AF37]">Bloom Clinic for Men</p>
                <h2 className="mt-3 text-2xl font-bold leading-tight md:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Pourquoi les hommes choisissent <span className="block">Bloom Clinic ?</span>
                </h2>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                {[
                  { icon: ShieldCheck, title: 'Expertise dédiée aux hommes' },
                  { icon: LockKeyhole, title: 'Consultation gratuite' },
                  { icon: Sparkles, title: 'Techniques modernes' },
                  { icon: UserRoundCheck, title: 'Accompagnement personnalisé' },
                ].map(({ icon: Icon, title }) => (
                  <div key={title} className="flex min-h-28 flex-col items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] px-3 py-4 text-center transition hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/10 sm:min-h-32 sm:px-5 sm:py-6">
                    <Icon size={24} strokeWidth={1.5} className="text-[#D4AF37] sm:size-7" />
                    <h3 className="mt-3 text-xs font-semibold leading-5 text-white sm:mt-4 sm:text-sm sm:leading-6">{title}</h3>
                  </div>
                ))}
              </div>
            </div>
          </section>

        <div className="relative overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(/images/backgrounds/bg-optimized.webp)',
              backgroundAttachment: 'fixed',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>

          <div className="relative z-10">
            {/* Avant / Après par service */}
            <ServicesBeforeAfter />

            <section id="expertise" className="border-y border-[#D4AF37]/20 bg-black/60 px-4 py-12 text-white md:py-16">
                <div className="mx-auto max-w-6xl">
                  <div className="mx-auto max-w-2xl text-center">
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#D4AF37]">La confiance se construit</p>
                    <h2 className="mt-3 text-3xl font-bold md:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Avis clients et preuves concrètes
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-white/60">
                      Des résultats visibles, une équipe attentive et des consultations pensées pour les hommes.
                    </p>
                  </div>

                  <div className="mt-8 grid gap-5 lg:grid-cols-[1.1fr_1fr]">
                    <article className="rounded-2xl border border-[#D4AF37]/40 bg-white/[0.05] p-6 md:p-8">
                      <div className="flex items-center gap-2 text-[#D4AF37]">
                        <span className="sr-only">5 étoiles sur 5</span>
                        {Array.from({ length: 5 }, (_, index) => <Star key={index} size={20} fill="currentColor" aria-hidden="true" />)}
                      </div>
                      <blockquote className="mt-5 text-xl leading-8 text-white md:text-2xl">
                        “Personnel professionnel, résultats visibles, équipe très accueillante.”
                      </blockquote>
                      <div className="mt-6 flex items-center gap-3 text-sm text-white/60">
                        <img src="/images/optimized/ui/googleAvis.webp" alt="Google" width={32} height={32} className="h-8 w-8 object-contain" />
                        <span>Avis client vérifié sur Google</span>
                      </div>
                    </article>

                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { icon: Star, value: '4,9/5', label: 'Note Google affichée' },
                        { icon: MapPinned, value: '2', label: 'Centres à Casablanca et Rabat' },
                        { icon: Users, value: '30K Clients accompagnés', label: 'Avec une équipe dédiée aux hommes' },
                        { icon: CalendarDays, value: 'Expertise médicale', label: 'Accompagnement au fil des années' },
                      ].map(({ icon: Icon, value, label }) => (
                        <div key={label} className="rounded-2xl border border-white/15 bg-white/[0.04] p-5">
                          <Icon size={22} className="text-[#D4AF37]" />
                          <p className="mt-4 text-lg font-semibold text-white">{value}</p>
                          <p className="mt-1 text-xs leading-5 text-white/55">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3">
                    <figure className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/15 bg-black">
                      <img
                        src={CLINIC_EXTERIOR_IMAGES[clinicImageIndex]}
                        srcSet={getResponsiveSrcSet(CLINIC_EXTERIOR_IMAGES[clinicImageIndex])}
                        alt="L’extérieur Bloom Clinic"
                        loading="lazy"
                        decoding="async"
                        width={640}
                        height={480}
                        sizes="(max-width: 768px) 50vw, 33vw"
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      />
                      <figcaption className="absolute inset-x-0 bottom-0 bg-black/65 px-3 py-2 text-center text-xs text-white/80">L’extérieur Bloom Clinic</figcaption>
                    </figure>

                    <figure className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/15 bg-black md:hidden">
                      <img
                        src={CLINIC_RECEPTION_IMAGES[receptionImageIndex]}
                        srcSet={getResponsiveSrcSet(CLINIC_RECEPTION_IMAGES[receptionImageIndex])}
                        alt="Accueil chaleureux et moderne"
                        loading="lazy"
                        decoding="async"
                        width={640}
                        height={480}
                        sizes="(max-width: 768px) 50vw, 33vw"
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      />
                      <figcaption className="absolute inset-x-0 bottom-0 bg-black/65 px-3 py-2 text-center text-xs text-white/80">Accueil chaleureux et moderne</figcaption>
                    </figure>

                    <figure className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/15 bg-black">
                      <img
                        src={CLINIC_MACHINE_IMAGES[machineImageIndex]}
                        srcSet={getResponsiveSrcSet(CLINIC_MACHINE_IMAGES[machineImageIndex])}
                        alt="Technologies modernes de soin"
                        loading="lazy"
                        decoding="async"
                        width={640}
                        height={480}
                        sizes="(max-width: 768px) 50vw, 33vw"
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      />
                      <figcaption className="absolute inset-x-0 bottom-0 bg-black/65 px-3 py-2 text-center text-xs text-white/80">Technologies modernes de soin</figcaption>
                    </figure>

                    <figure className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/15 bg-black">
                      <img
                        src={CLINIC_INTERIOR_IMAGES[interiorImageIndex]}
                        srcSet={getResponsiveSrcSet(CLINIC_INTERIOR_IMAGES[interiorImageIndex])}
                        alt="Espaces de soin confortables"
                        loading="lazy"
                        decoding="async"
                        width={640}
                        height={480}
                        sizes="(max-width: 768px) 50vw, 33vw"
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                      />
                      <figcaption className="absolute inset-x-0 bottom-0 bg-black/65 px-3 py-2 text-center text-xs text-white/80">Espaces de soin confortables</figcaption>
                    </figure>
                  </div>

                  <div className="mt-8 flex flex-col items-center justify-center gap-3 text-center sm:flex-row">
                    <a href="tel:0662251047" className="inline-flex items-center gap-2 rounded-full border border-white/35 px-5 py-3 text-sm font-semibold text-white transition hover:border-[#D4AF37] hover:text-[#D4AF37]">
                      <Users size={17} /> Parler à un spécialiste
                    </a>
                    <a href="#location" className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37] px-5 py-3 text-sm font-semibold text-[#D4AF37] transition hover:bg-[#D4AF37] hover:text-black">
                      <MapPinned size={17} /> Voir nos localisations Google Maps
                    </a>
                    <a href="#appointment" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3 text-sm font-semibold text-black transition hover:bg-[#e7c766]">
                      <ExternalLink size={17} /> Recevoir un devis personnalisé
                    </a>
                  </div>
                </div>
              </section>

            {/* Nos adresses */}
            <LocationsSection />
          </div>
        </div>

        {/* Section Vidéos */}
        {isVideoCarouselEnabled && <VideosSection />}
        </Suspense>}
      </main>

      {/* Footer */}
      {showDeferredContent && <Suspense fallback={null}><Footer /></Suspense>}

      {showDeferredContent && <Suspense fallback={null}><FloatingActionButtons /></Suspense>}

      {/* CSS Animations */}
      <style>{`
        @keyframes floatBackground {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.4;
          }
          25% {
            transform: translate(30px, -30px) scale(1.05);
            opacity: 0.5;
          }
          50% {
            transform: translate(60px, 0) scale(1);
            opacity: 0.4;
          }
          75% {
            transform: translate(30px, 30px) scale(0.95);
            opacity: 0.5;
          }
        }

        @keyframes floatBackgroundSlow {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
            opacity: 0.3;
          }
          50% {
            transform: translate(-50px, 50px) rotate(180deg);
            opacity: 0.4;
          }
        }

        @keyframes floatBackgroundFast {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.35;
          }
          50% {
            transform: translate(100px, -100px) scale(1.1);
            opacity: 0.45;
          }
        }
      `}</style>
    </div>
  );
}
